import { safeDivision } from '../lib/helpers';
// ---- internal imports ----

class FinancialRatios {
	constructor(
		netProfit, grossProfit, revenue,
		totalAssets, totalEquity, costOfGoodsSold, 
		averageTotalAssets, averageInventory,
	) {
		this.netProfit = netProfit;
		this.grossProfit = grossProfit;
		this.revenue = revenue;
		this.totalAssets = totalAssets;
		this.totalEquity = totalEquity;
		this.costOfGoodsSold = costOfGoodsSold;
		this.averageTotalAssets = averageTotalAssets;
		this.averageInventory = averageInventory;
	}

	get returnOnAssets() {
		return safeDivision(this.netProfit * 100, this.totalAssets);
	}

	get returnOnEquity() {
		return safeDivision(this.netProfit * 100, this.totalEquity);
	}

	get grossProfitMargin() {
		return safeDivision(this.grossProfit * 100, this.revenue);
	}

	get netProfitMargin() {
		return safeDivision(this.netProfit * 100, this.revenue);
	}

	get assetTurnoverRatio() {
		return safeDivision(this.revenue, this.averageTotalAssets);
	}

	get inventoryTurnoverRatio() {
		return safeDivision(this.costOfGoodsSold, this.averageInventory);
	}

	toPlainObj() {
		return {
			netProfit: this.netProfit,
			grossProfit: this.grossProfit,
			revenue: this.revenue,
			totalAssets: this.totalAssets,
			totalEquity: this.totalEquity,
			costOfGoodsSold: this.costOfGoodsSold,
			averageTotalAssets: this.averageTotalAssets,
			averageInventory: this.averageInventory,

			returnOnAssets: this.returnOnAssets,
			returnOnEquity: this.returnOnEquity,
			grossProfitMargin: this.grossProfitMargin,
			netProfitMargin: this.netProfitMargin,
			assetTurnoverRatio: this.assetTurnoverRatio,
			inventoryTurnoverRatio: this.inventoryTurnoverRatio,
		};
	}
};

function FinancialRatiosBuilder() {
	return {
		withNetProfit: function (netProfit) {
			this.netProfit = netProfit;
			return this;
		},
		withGrossProfit: function (grossProfit) {
			this.grossProfit = grossProfit;
			return this;
		},
		withRevenue: function (revenue) {
			this.revenue = revenue;
			return this;
		},
		withTotalAssets: function (totalAssets) {
			this.totalAssets = totalAssets;
			return this;
		},
		withTotalEquity: function (totalEquity) {
			this.totalEquity = totalEquity;
			return this;
		},
		withCostOfGoodsSold: function (costOfGoodsSold) {
			this.costOfGoodsSold = costOfGoodsSold;
			return this;
		},
		withAverageTotalAssets: function (averageTotalAssets) {
			this.averageTotalAssets = averageTotalAssets;
			return this;
		},
		withAverageInventory: function (averageInventory) {
			this.averageInventory = averageInventory;
			return this;
		},
		from: function (
			incomeStatement, balanceSheet,
			prevQuarterTotalAssets = 0,
		) {
			const averageTotalAssets =
				Math.round((prevQuarterTotalAssets + balanceSheet?.totalAssets) / 2);
			const averageInventory =
				Math.round((incomeStatement?.openingInventory + incomeStatement?.closingInventory) / 2);

			this.withNetProfit(incomeStatement?.netProfit);
			this.withGrossProfit(incomeStatement?.grossProfit);
			this.withRevenue(incomeStatement?.revenue);
			this.withTotalAssets(balanceSheet?.totalAssets);
			this.withTotalEquity(balanceSheet?.totalEquity);
			this.withCostOfGoodsSold(incomeStatement?.costOfGoodsSold);
			this.withAverageTotalAssets(averageTotalAssets);
			this.withAverageInventory(averageInventory);

			return this;
		},
		build: function () {
			return new FinancialRatios(
				this.netProfit, this.grossProfit, this.revenue,
				this.totalAssets, this.totalEquity, this.costOfGoodsSold, 
				this.averageTotalAssets, this.averageInventory,
			);
		}
	};
}

function getEmptyFinancialRatios() {
	return new FinancialRatios();
}


export {
	getEmptyFinancialRatios
};

export default FinancialRatiosBuilder;
